<template>
  <g :class="['pg-highlight', highlightClass]">
    <mce-line
      :class="['pf-highlight-line', lineStyles]"
      :x1="configStartX1"
      :x2="configStartX2"
      :y1="configStartY1"
      :y2="configStartY2"
      @mousedown="configuration ? move($event, 'start') : null"
    />
    <mce-rectangle
      v-if="end"
      :class="['pf-highlight-fill', rectStyles]"
      :width="width"
      :height="height"
      :x="horizontal ? rangeX[0] : configStartX1 < configEndX1 ? configStartX1 : configEndX1"
      :y="horizontal ? (configStartY1 < configEndY1 ? configStartY1 : configEndY1) : null"
      :fill="getFill"
      :fill-opacity="fillOpacity"
    />
    <mce-rectangle
      v-if="configuration && this.pattern"
      id="pf-highlight-gradient"
      :class="['pf-highlight-fill', rectStyles]"
      :width="width"
      :height="height"
      :x="horizontal ? rangeX[0] : configStartX1 < configEndX1 ? configStartX1 : configEndX1"
      :y="horizontal ? (configStartY1 < configEndY1 ? configStartY1 : configEndY1) : null"
      :fill="`url(#pattern-${id}-gradient)`"
    />
    <defs>
      <pattern :id="`pattern-${id}-gradient`" patternUnits="userSpaceOnUse" width="8" height="8">
        <path d="M-1,1 l4,-4 M0,8 l8,-8 M6,10 l4,-4" stroke="#0077CF" />
      </pattern>
      <pattern :id="`pattern-${id}`" patternUnits="userSpaceOnUse" width="8" height="8">
        <path d="M-1,1 l4,-4 M0,8 l8,-8 M6,10 l4,-4" :stroke="color" />
      </pattern>
    </defs>
    <mce-line
      v-if="end"
      :class="['pf-highlight-line', lineStyles]"
      :x1="configEndX1"
      :x2="configEndX2"
      :y1="configEndY1"
      :y2="configEndY2"
      @mousedown="configuration ? move($event, 'end') : null"
    />
    <g>
      <mce-rectangle
        v-if="selected && configuration"
        class="pf-highlight-select_side"
        :width="handleWidth"
        :height="handleHeight"
        :x="topX"
        :y="topY"
        :stroke="handleColor"
        :stroke-width="2"
        fill="white"
        @mousedown="configuration ? move($event, 'start') : null"
      />
      <mce-rectangle
        v-if="selected && configuration"
        class="pf-highlight-select_side"
        :width="handleWidth"
        :height="handleHeight"
        :x="bottomX"
        :y="bottomY"
        :stroke="handleColor"
        :stroke-width="2"
        fill="white"
        @mousedown="configuration ? move($event, 'end') : null"
      />
      <pf-position-tag
        v-if="selected && configuration"
        @mousedown.native="configuration ? move($event, 'start') : null"
        :tag-x="tagX"
        :tag-y="tagY"
        :text-x="numberX"
        :text-y="numberY"
        :text="tagOne"
        :width="tagWidth"
        :height="tagHeight"
      ></pf-position-tag>
      <pf-position-tag
        v-if="selected && configuration"
        @mousedown.native="configuration ? move($event, 'end') : null"
        :tag-x="tagX2"
        :tag-y="tagY2"
        :text-x="numberX2"
        :text-y="numberY2"
        :text="tagTwo"
        :width="tagWidth"
        :height="tagHeight"
      ></pf-position-tag>
    </g>
  </g>
</template>

<script>
  import { scaleOrdinal } from 'd3-scale';
  import formatValue from '@/utils/valueFormatter';
  import countDecimals from '@/utils/countDecimals';

  import MceLine from '@mce/line';
  import MceRectangle from '@mce/rectangle';
  import { performance } from '@mce/color';
  import PfPositionTag from './PositionTag.vue';
  import repoMixin from '../../mixins/interactions/reposition';

  // TODO: click listener on selection

  export default {
    name: 'PfHighlight',
    components: {
      MceLine,
      MceRectangle,
      PfPositionTag,
    },
    mixins: [repoMixin],
    props: {
      id: {
        type: String,
      },
      bandX: {
        type: Array,
      },
      configuration: {
        type: Boolean,
      },
      domainX: {
        type: Array,
        required: true,
      },
      domainY: {
        type: Array,
        required: true,
      },
      horizontal: {
        type: Boolean,
        default: false,
      },
      patternId: {
        type: Array,
        default: () => ['pattern-3'],
      },
      color: {
        type: String,
        default: '#5e5e5e',
      },
      selected: {
        type: Boolean,
      },
      start: {
        type: Object,
      },
      end: {
        type: Object,
      },
      opacity: {
        type: Number,
        default: 0.2,
      },
      pattern: {
        type: Boolean,
      },
      plotStart: {
        type: Object,
      },
      plotEnd: {
        type: Object,
      },
      outlines: {
        type: Object,
      },
      rangeX: {
        type: Array,
        required: true,
      },
      rangeY: {
        type: Array,
        required: true,
      },
      transpose: {
        type: Boolean,
        default: false,
      },
      scaleTypeX: {
        type: String,
        default: 'linear',
      },
      scaleTypeY: {
        type: String,
        default: 'linear',
      },
      setEnd: {
        type: Boolean,
      },
      valueFormat: {
        type: Object,
      },
    },
    computed: {
      handleWidth() {
        return this.horizontal ? this.hFull : this.hSide;
      },
      handleHeight() {
        return this.horizontal ? this.hSide : this.hFull;
      },
      bottomX() {
        return this.horizontal
          ? (this.rangeX[0] + this.rangeX[1]) / 2 - this.hFull / 2
          : this.configEndX1;
      },
      bottomY() {
        return this.horizontal
          ? this.configEndY1 - this.hPadding
          : this.height / 2 - this.hFull / 2;
      },
      topX() {
        return this.horizontal
          ? (this.rangeX[0] + this.rangeX[1]) / 2 - this.hFull / 2
          : this.configStartX1 - this.hSide;
      },
      topY() {
        return this.horizontal
          ? this.configStartY1 - this.hPadding
          : this.height / 2 - this.hFull / 2;
      },
      highlightClass() {
        const obj = {};
        obj['pf-highlight--config'] = this.configuration;

        return obj;
      },
      lineStyles() {
        const obj = {};
        obj['pf-highlight-line--config'] = this.configuration;
        obj['pf-highlight-line--selected'] = this.selected && this.configuration;

        return obj;
      },
      fillOpacity() {
        return this.pattern ? 1 : this.opacity;
      },
      fillScale() {
        const fill = `pattern-${this.id}` ?? this.color;
        return scaleOrdinal().range([fill]);
      },
      getFill() {
        return this.pattern ? `url(#${this.fillScale()})` : `${this.color}`;
      },
      rectStyles() {
        const obj = {};
        obj['pf-highlight-fill--config'] = this.configuration;

        return obj;
      },
      configStartX1() {
        const x =
          this.configuration && this.dynamicStart != null && this.dynamicStart.x != null
            ? this.dynamicStart.x
            : this.plotStartX;
        return this.horizontal ? this.rangeX[0] : x;
      },
      configStartX2() {
        const x =
          this.configuration && this.dynamicStart != null && this.dynamicStart.x != null
            ? this.dynamicStart.x
            : this.plotStartX;
        return this.horizontal ? this.rangeX[1] : x;
      },
      configEndX1() {
        const x =
          this.configuration && this.dynamicEnd != null && this.dynamicEnd.x != null
            ? this.dynamicEnd.x
            : this.plotEndX;
        return this.horizontal ? this.rangeX[0] : x;
      },
      configEndX2() {
        const x =
          this.configuration && this.dynamicEnd != null && this.dynamicEnd.x != null
            ? this.dynamicEnd.x
            : this.plotEndX;
        return this.horizontal ? this.rangeX[1] : x;
      },
      configStartY1() {
        const y =
          this.configuration && this.dynamicStart != null && this.dynamicStart.y != null
            ? this.dynamicStart.y
            : this.plotStartY;
        return this.horizontal ? y : this.rangeY[0];
      },
      configStartY2() {
        const y =
          this.configuration && this.dynamicStart != null && this.dynamicStart.y != null
            ? this.dynamicStart.y
            : this.plotStartY;
        return this.horizontal ? y : 0;
      },
      configEndY1() {
        const y =
          this.configuration && this.dynamicEnd != null && this.dynamicEnd.y != null
            ? this.dynamicEnd.y
            : this.plotEndY;
        return this.horizontal ? y : this.rangeY[0];
      },
      configEndY2() {
        const y =
          this.configuration && this.dynamicEnd != null && this.dynamicEnd.y != null
            ? this.dynamicEnd.y
            : this.plotEndY;
        return this.horizontal ? y : 0;
      },
      width() {
        const diffX = Math.abs(this.configStartX1 - this.configEndX1);

        return this.horizontal ? Math.abs(this.rangeX[1] - this.rangeX[0]) : diffX;
      },
      height() {
        const diffY = Math.abs(this.configStartY1 - this.configEndY1);
        return this.horizontal ? diffY : this.rangeY[0];
      },
      plotStartX() {
        return this.plotStart != null ? this.getScaleX(this.plotStart.x) : this.start.x;
      },
      plotEndX() {
        return this.plotEnd != null ? this.getScaleX(this.plotEnd.x) : this.end.x;
      },
      plotStartY() {
        return this.plotStart != null ? this.yScale(this.plotStart.y) : this.start.y;
      },
      plotEndY() {
        return this.plotEnd != null ? this.yScale(this.plotEnd.y) : this.end.y;
      },
      tagOffset() {
        return !this.horizontal && this.scaleTypeX === 'time' ? 30 : 10;
      },
      tagOne() {
        let value = this.horizontal
          ? this.setYPosition(this.configStartY1)
          : this.setXPosition(this.configStartX1);

        if (typeof value !== 'number') {
          const d = new Date(value);
          return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
        }

        value = countDecimals(value) > 2 ? value.toFixed(2) : value;

        if (
          (this.horizontal && this.scaleTypeY === 'linear') ||
          (!this.horizontal && this.scaleTypeX === 'linear')
        ) {
          return formatValue(value, this.valueFormat);
        }

        return value;
      },
      tagX() {
        const start = this.horizontal ? this.configStartX1 : this.configStartX2;
        const offset = this.horizontal ? this.tagWidth : this.tagWidth / 2;

        return start - offset;
      },
      tagY() {
        const offset = this.horizontal ? -10 : 2;
        return this.configStartY1 + offset;
      },
      numberX() {
        const start = this.horizontal ? this.configStartX1 : this.configStartX2;
        const offset = this.horizontal ? this.tagWidth / 2 + this.tagOffset : this.tagOffset;
        return start - offset;
      },
      numberY() {
        const offset = this.horizontal ? 6 : 20;
        return this.configStartY1 + offset;
      },
      tagTwo() {
        let value = this.horizontal
          ? this.setYPosition(this.configEndY1)
          : this.setXPosition(this.configEndX1);

        if (typeof value !== 'number') {
          const d = new Date(value);
          return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
        }

        value = countDecimals(value) > 2 ? value.toFixed(2) : value;

        if (
          (this.horizontal && this.scaleTypeY === 'linear') ||
          (!this.horizontal && this.scaleTypeX === 'linear')
        ) {
          return formatValue(value, this.valueFormat);
        }

        return value;
      },
      tagX2() {
        const start = this.horizontal ? this.configEndX1 : this.configEndX2;
        const offset = this.horizontal ? this.tagWidth : this.tagWidth / 2;

        return start - offset;
      },
      tagY2() {
        const offset = this.horizontal ? -10 : 2;
        return this.configEndY1 + offset;
      },
      numberX2() {
        const start = this.horizontal ? this.configEndX1 : this.configEndX2;
        const offset = this.horizontal ? this.tagWidth / 2 + this.tagOffset : this.tagOffset;
        return start - offset;
      },
      numberY2() {
        const offset = this.horizontal ? 6 : 20;
        return this.configEndY1 + offset;
      },
    },
    data() {
      return {
        hFull: 100,
        hSide: 15,
        hPadding: 7,
        init: false,
        tagWidth: 90,
        tagHeight: 22,
        handleColor: '#0077CF',
        patternStroke: performance.neutral,
      };
    },
    watch: {
      plotStart() {
        if (this.init) {
          this.dynamicStart = this.start;
        }
      },
      plotEnd() {
        if (this.init) {
          this.dynamicEnd = this.end;
          this.init = false;
        }
      },
      setEnd(val) {
        if (val) {
          this.updatePosition('plotStart', { x: this.start.x, y: this.start.y });
          this.updatePosition('plotEnd', { x: this.end.x, y: this.end.y });
        }
      },
      transpose(val) {
        this.transposeScale = val;
      },
    },
    methods: {
      move(ev, side) {
        const isStart = side === 'start';
        this.configProp = isStart ? 'plotStart' : 'plotEnd';
        this.repositioning(ev);
      },
    },
    mounted() {
      this.configProp = 'plotStart';
      this.transposeScale = this.transpose;
      if (this.configuration && !this.plotStart) {
        this.init = true;
        this.updatePosition('plotStart', { x: this.start.x, y: this.start.y });
        this.updatePosition('plotEnd', { x: this.end.x, y: this.end.y });
      }
    },
  };
</script>

<style lang="scss">
  @import '@mce/visual-language';
  @import '@mds/constants';

  #pf-highlight-gradient {
    opacity: 0;
    display: inline-block;
  }

  .pf-highlight--config {
    #pf-highlight-gradient:hover {
      opacity: 1;
    }
  }

  .pf-highlight {
    &-line {
      &--config:hover {
        shape-rendering: auto;
        opacity: 1;
        cursor: grab;
      }

      &--selected {
        shape-rendering: auto;
        opacity: 1;
        stroke: $mds-interactive-color-primary-default;
        stroke-width: 2;
      }
    }

    &-fill--config {
      cursor: pointer;

      &:hover {
        stroke: $mds-interactive-color-primary-default;
        stroke-width: 2;
      }
    }

    &-select_side {
      cursor: grab;
    }
  }
</style>
